/**
 * The words the Tool uses for Backbone's vocabularies, in one place so every
 * view says the same thing.
 */
import type { PublicResponseStatus } from "./api";
import { isNonpartisanParty, normalizeCandidacyParty } from "../../lib/candidacy-party";
import { h } from "./dom";

export const RESPONSE_LABEL: Record<PublicResponseStatus, string> = {
  completed: "Completed",
  no_response: "No response",
  could_not_reach: "Could not reach",
  refused: "Refused",
};

/** `no_response` → `no-response`, the contract's class spelling. */
export const responseClass = (s: PublicResponseStatus) => s.replace(/_/g, "-");

export function responseBadge(status: PublicResponseStatus): HTMLElement {
  return h("span", { class: `bsc-status bsc-status--${responseClass(status)}` }, RESPONSE_LABEL[status]);
}

/**
 * A candidacy status worth a pill on a ballot. `candidate` (simply on the
 * ballot) says nothing a reader needs, so it has none. Legacy values from
 * the older vocabulary map onto their current meaning.
 */
export function candidacyStatusLabel(status: string | null, stage: string): string | null {
  switch (status) {
    case "prospective": return "Prospective";
    case "announced": return "Announced";
    case "write_in": return "Write-In";
    case "challenged": return "Ballot challenged";
    case "removed": return "Removed from ballot";
    case "withdrawn":
    case "withdrew": return "Withdrawn";
    case "withdrawn_on_ballot": return "Withdrawn (still on ballot)";
    case "advanced": return "Advanced";
    case "lost": return stage === "primary" ? "Lost the primary" : "Lost";
    case "won": return stage === "primary" ? "Won the primary" : "Won";
    case "advanced_write_in":
    case "lost_write_in":
    case "won_write_in": return `${candidacyStatusLabel(status.replace("_write_in", ""), stage)} (write-in)`;
    default: return null;
  }
}

/**
 * Canonical status for data-status and result logic, folding legacy
 * spellings, write-in results into their result, and an on-ballot
 * withdrawal into Withdrawn. Labels come from candidacyStatusLabel.
 */
export function candidacyStatusValue(status: string | null): string | null {
  if (status === "withdrew" || status === "withdrawn_on_ballot") return "withdrawn";
  if (status?.endsWith("_write_in")) return status.slice(0, -"_write_in".length);
  if (status === "active" || status === "incumbent") return "candidate";
  return status;
}

export function endorsedBadge(): HTMLElement {
  return h("span", { class: "bsc-badge bsc-badge--endorsed" }, "Endorsed");
}

/** A revocation is its own state, never a missing badge (ADR 0013). */
export function revokedBadge(): HTMLElement {
  return h("span", { class: "bsc-badge bsc-badge--revoked" }, "Endorsement withdrawn");
}

export function incumbentBadge(): HTMLElement {
  return h("span", { class: "bsc-badge bsc-badge--incumbent" }, "Incumbent");
}

/**
 * Parties with their own pill color, matching Legislator Lookup's palette
 * (bsc-legislator-lookup lib/party-styles.ts). Any other name gets the grey pill.
 */
const PARTY_TONE: Record<string, string> = {
  democratic: "democratic",
  republican: "republican",
  libertarian: "libertarian",
  green: "green",
  "working families": "working-families",
  "working families party": "working-families",
  independent: "independent",
};

/** Canonical display name for a Candidacy's Party; null when nonpartisan. */
export function partyName(raw: string | null | undefined): string | null {
  const party = normalizeCandidacyParty(raw);
  return party && !isNonpartisanParty(party) ? party : null;
}

/**
 * Party as a one-letter pill that widens to the full name on hover. Purely
 * visual: the caller puts the party name in the accessibility tree where it
 * reads naturally, after the candidate's name.
 */
export function partyBadge(party: string): HTMLElement {
  const [first, ...rest] = Array.from(party);
  return h(
    "span",
    { class: "bsc-party", "data-party": PARTY_TONE[party.toLowerCase()] ?? "other", "aria-hidden": "true" },
    h("span", { class: "bsc-party__initial" }, first.toUpperCase()),
    h("span", { class: "bsc-party__rest" }, h("span", null, rest.join("")))
  );
}
